import * as React from "react";
import {useCallback, useState} from "react";

import {useGuideState} from "@khan/guide-state";
import {
    FeedbackType,
    type ChatInputMetadata,
    type HideChatOptions,
    type ImageInput,
    type InteractionID,
} from "@khan/guide-types";

import type {ChatInputComponent} from "./chat-input/chat-input.tsx";
import {Chat} from "./chat.tsx";
import {FeedbackForm} from "./feedback-form.tsx";
import {removeAttachedImagesFromGuideState} from "./image-attach/remove-attached-images-from-guide-state.ts";
import {TextToSpeechOnboardingProvider} from "./text-to-speech/text-to-speech-onboarding-provider.tsx";

interface Props {
    conversationRecordedNotice?: React.ReactNode;
    forceHideChatOptions?: HideChatOptions;
    expanded?: boolean;
    onMessageSend: (
        message: string,
        imagesAttached: Array<ImageInput>,
        metadata: ChatInputMetadata,
    ) => unknown;
    skipSpeechOnboarding?: boolean;
    ChatInputComponent?: ChatInputComponent;
}

/**
 * The main content of the chat window.
 */
export const ChatMainContent = ({
    conversationRecordedNotice,
    forceHideChatOptions,
    expanded,
    onMessageSend,
    skipSpeechOnboarding,
    ChatInputComponent,
}: Props): React.ReactElement => {
    const isLoading = useGuideState("isLoading");
    const threadId = useGuideState("threadId");

    const images = useGuideState("images");
    // Newly attached images do not have interactionIDs.
    const newlyAttachedImage = images
        .filter(({interactionID}) => !interactionID)
        .at(-1);
    const ratedInteractions = useGuideState("ratedInteractions");
    // We need the interactionId iff we're going to fill out the feedback form
    // for a specific message.
    const [feedbackInteractionId, setFeedbackInteractionId] = useState<
        InteractionID | null | undefined
    >(null);

    const interaction =
        feedbackInteractionId != null
            ? ratedInteractions.get(feedbackInteractionId)
            : null;
    const disabledWrittenFeedbackType =
        interaction?.sentiment === "thumbs-up"
            ? FeedbackType.Negative
            : interaction?.sentiment === "thumbs-down"
              ? FeedbackType.Positive
              : undefined;

    const handleCloseFeedback = useCallback(() => {
        setFeedbackInteractionId(null);
    }, [setFeedbackInteractionId]);

    const handleSend = useCallback(
        (message: string, metadata: ChatInputMetadata) => {
            if (!message || isLoading) {
                return;
            }

            const imagesAttached = newlyAttachedImage
                ? [
                      {
                          ...newlyAttachedImage,
                          isNewInput: true,
                      },
                  ]
                : [];
            onMessageSend(message, imagesAttached, metadata);
            removeAttachedImagesFromGuideState();
        },
        [isLoading, newlyAttachedImage, onMessageSend],
    );

    const handleOpenFeedbackForm = useCallback(
        (interactionId: string) => {
            setFeedbackInteractionId(interactionId);
        },
        [setFeedbackInteractionId],
    );

    if (feedbackInteractionId) {
        return (
            <FeedbackForm
                disabledWrittenFeedbackType={disabledWrittenFeedbackType}
                threadId={threadId ?? ""}
                interactionId={feedbackInteractionId}
                onClose={handleCloseFeedback}
            />
        );
    }

    return (
        <TextToSpeechOnboardingProvider>
            <Chat
                conversationRecordedNotice={conversationRecordedNotice}
                disableSend={isLoading}
                expanded={expanded}
                forceHideChatOptions={forceHideChatOptions}
                onMessageSend={handleSend}
                onOpenFeedbackForm={handleOpenFeedbackForm}
                skipSpeechOnboarding={skipSpeechOnboarding}
                ChatInputComponent={ChatInputComponent}
            />
        </TextToSpeechOnboardingProvider>
    );
};
